import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarEventChip } from '@/pages/calendar/CalendarEventChip';
import { DayDetailPopover } from '@/pages/calendar/DayDetailPopover';
import { sortDayEvents } from '@/lib/calendarDate';
import type { CalendarEvent } from '@/types/shared';
import './CalendarGrid.css';

const WEEKDAY_KEYS = ['sat', 'sun', 'mon', 'tue', 'wed', 'thu', 'fri'] as const;

interface CalendarGridProps {
  weeks: string[][];
  eventsByDate: Map<string, CalendarEvent[]>;
  monthKey: string;
  todayStr: string;
  language: string;
  captionText: string;
}

/** T104 §2.4 density cap — the max chips a day cell shows before "+N more". Below `bp.md` the
 * grid itself is hidden in favor of `CalendarAgenda` (no cap there), so this only ever needs to
 * resolve the 3 desktop/tablet tiers. */
function chipCapForWidth(width: number): number {
  if (width >= 1280) return 4; // bp.xl
  if (width >= 1024) return 3; // bp.lg
  return 2; // bp.md..bp.lg (condensed grid)
}

function useChipCap(): number {
  const [cap, setCap] = useState(() =>
    typeof window === 'undefined' ? 4 : chipCapForWidth(window.innerWidth),
  );

  useEffect(() => {
    function onResize() {
      setCap(chipCapForWidth(window.innerWidth));
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return cap;
}

/**
 * T104 §4.1/§7.1 — a plain semantic `<table>` (not `role="grid"`, see §7.1's rationale), Saturday-
 * first, out-of-month spill days dimmed-but-interactive, current-day highlighted with
 * `aria-current="date"`. Every interactive thing inside a cell is a real, natively focusable
 * element reached in normal `Tab`/reading order — RTL-safe with zero custom key handling.
 */
export function CalendarGrid({
  weeks,
  eventsByDate,
  monthKey,
  todayStr,
  language,
  captionText,
}: CalendarGridProps) {
  const { t } = useTranslation();
  const cap = useChipCap();
  const [openDay, setOpenDay] = useState<string | null>(null);
  const triggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  function closePopover(dateStr: string) {
    setOpenDay(null);
    triggerRefs.current.get(dateStr)?.focus();
  }

  return (
    <table className="fa-cal-grid">
      <caption className="fa-visually-hidden">{captionText}</caption>
      <thead>
        <tr>
          {WEEKDAY_KEYS.map((key) => (
            <th key={key} scope="col">
              {t(`calendar.weekdaysShort.${key}`)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {weeks.map((week) => (
          <tr key={week[0]}>
            {week.map((dateStr) => {
              const dayEvents = sortDayEvents(eventsByDate.get(dateStr) ?? []);
              const isCurrentMonth = dateStr.slice(0, 7) === monthKey;
              const isToday = dateStr === todayStr;
              const visible = dayEvents.slice(0, cap);
              const overflowCount = dayEvents.length - visible.length;
              const dayNumber = Number(dateStr.slice(-2));
              const popoverId = `fa-cal-popover-${dateStr}`;
              const popoverHeadingId = `${popoverId}-heading`;

              return (
                <td
                  key={dateStr}
                  className={`fa-cal-grid__cell${
                    isCurrentMonth ? '' : ' fa-cal-grid__cell--spill'
                  }${isToday ? ' fa-cal-grid__cell--today' : ''}`}
                  aria-current={isToday ? 'date' : undefined}
                >
                  <time dateTime={dateStr} className="fa-cal-grid__date">
                    {dayNumber}
                  </time>

                  {dayEvents.length > 0 && (
                    <ul className="fa-cal-grid__chips">
                      {visible.map((event) => (
                        <li key={event.id}>
                          <CalendarEventChip event={event} language={language} variant="compact" />
                        </li>
                      ))}
                    </ul>
                  )}

                  {overflowCount > 0 && (
                    <div className="fa-cal-grid__overflow-wrap">
                      <button
                        type="button"
                        ref={(el) => {
                          if (el) triggerRefs.current.set(dateStr, el);
                          else triggerRefs.current.delete(dateStr);
                        }}
                        data-cal-overflow-trigger="true"
                        className="fa-cal-grid__overflow-btn"
                        aria-haspopup="dialog"
                        aria-expanded={openDay === dateStr}
                        aria-controls={popoverId}
                        onClick={() =>
                          setOpenDay((current) => (current === dateStr ? null : dateStr))
                        }
                      >
                        {t('calendar.overflow.moreTemplate', { count: overflowCount })}
                      </button>

                      {openDay === dateStr && (
                        <DayDetailPopover
                          id={popoverId}
                          headingId={popoverHeadingId}
                          dateStr={dateStr}
                          events={dayEvents}
                          language={language}
                          onClose={() => closePopover(dateStr)}
                        />
                      )}
                    </div>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
